import React, { useState, useEffect } from "react";
import Table from "@mui/joy/Table";
import { Box, Button, Stack, Typography } from "@mui/joy";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import { ModalDialog } from "@mui/joy";
import Divider from "@mui/joy/Divider";
import { FormControl, FormLabel } from "@mui/joy";
import Input from "@mui/joy/Input";
import { db } from "../../FirebaseConfig";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import StarsIcon from "@mui/icons-material/Stars";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";

interface Hint {
  hint: string;
  points: string;
}

interface Challenge {
  id: number;
  title: string;
  category: string;
  difficulty: string;
  points: string;
  hints?: Hint[];
}

interface CTFChallengeTableInfoPageProps {
  challenges: Challenge[];
  eventname: string;
}

const CTFChallengeTableInfoPage: React.FC<CTFChallengeTableInfoPageProps> = ({
  challenges,
  eventname,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(
    null
  );
  const [hint, setHint] = useState<string>("");
  const [hintPoints, setHintPoints] = useState<string>("0");
  const [hints, setHints] = useState<Hint[]>([]); // State to store hints array

  // Handle opening the modal and setting the selected challenge
  const handleOpenModal = (challenge: Challenge) => {
    setSelectedChallenge(challenge);
    setHints(challenge.hints || []); // Load existing hints
    setOpen(true);
  };

  // Update the challenge's hints in Firestore
  const updateChallenge = async (updatedHints: Hint[]) => {
    if (!selectedChallenge) {
      console.error("No challenge selected!");
      return;
    }

    try {
      const eventDocRef = doc(db, "Events", eventname.toLowerCase());
      const eventDoc = await getDoc(eventDocRef);

      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        const ctfChallenges: Challenge[] = eventData.ctfChallenges;

        const challengeIndex = ctfChallenges.findIndex(
          (challenge) => challenge.id === selectedChallenge.id
        );

        if (challengeIndex !== -1) {
          ctfChallenges[challengeIndex].hints = updatedHints;
          await updateDoc(eventDocRef, { ctfChallenges });
          setOpen(false);
          toast.success("Challenge hints updated successfully!");
          // Reload the page after 5 seconds
          setTimeout(() => {
            window.location.reload();
          }, 5000);
        } else {
          console.error("Challenge not found!");
          toast.error("Challenge not found!");
        }
      } else {
        console.error("Event not found!");
        toast.error("Event not found!");
      }
    } catch (error) {
      console.error("Error updating challenge hints: ", error);
    }
  };

  // Add a new hint to the local hints array
  const handleAddHint = () => {
    if (hint.trim() === "" || hintPoints.trim() === "") {
      console.error("Hint or points cannot be empty!");
      return;
    }

    setHints([...hints, { hint, points: hintPoints }]);
    setHint("");
    setHintPoints("0");
  };

  // Delete a hint from the local hints array
  const handleDeleteHint = (index: number) => {
    const updatedHints = hints.filter((_, i) => i !== index);
    setHints(updatedHints);
  };

  // Save the current hints to Firestore
  const handleSaveHints = () => {
    updateChallenge(hints);
  };

  // Modify an existing hint in the local state
  const handleModifyHint = (
    index: number,
    newHint: string,
    newPoints: string
  ) => {
    const updatedHints = [...hints];
    updatedHints[index] = { hint: newHint, points: newPoints };
    setHints(updatedHints);
  };

  return (
    <>
      <Table aria-label="basic table" sx={{ textAlign: "center" }}>
        <thead style={{ textAlign: "center" }}>
          <tr>
            <th style={{ width: "30%", textAlign: "center" }}>Name</th>
            <th style={{ width: "20%", textAlign: "center" }}>Category</th>
            <th style={{ width: "20%", textAlign: "center" }}>Difficulty</th>
            <th style={{ width: "20%", textAlign: "center" }}>Points</th>
            <th style={{ width: "20%", textAlign: "center" }}>Hints</th>
          </tr>
        </thead>
        <tbody>
          {challenges.map((challenge) => (
            <tr key={challenge.id}>
              <td>{challenge.title}</td>
              <td>{challenge.category}</td>
              <td>{challenge.difficulty}</td>
              <td>{challenge.points}</td>
              <td>
                <Button
                  color="primary"
                  size="md"
                  variant="outlined"
                  onClick={() => handleOpenModal(challenge)}
                >
                  Edit Hints
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {selectedChallenge && (
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ModalDialog layout="center" size={"lg"}>
            <Typography level="h2">
              Modify Hints for {selectedChallenge.title}
            </Typography>
            <ModalClose />
            <Box
              sx={{
                display: "flex",
                border: "1px solid",
                borderRadius: "7px",
                borderColor: "#363636",
                padding: "20px",
              }}
            >
              <Stack spacing={1} width="100%" divider={<Divider />}>
                {/* Show existing hints */}
                {hints.map((hintItem, index) => (
                  <Stack
                    key={index}
                    spacing={1}
                    direction="row"
                    alignItems="center"
                  >
                    <Typography>Hint {index + 1}:</Typography>
                    <Input
                      size="sm"
                      value={hintItem.hint}
                      onChange={(e) =>
                        handleModifyHint(index, e.target.value, hintItem.points)
                      }
                    />
                    <Input
                      size="sm"
                      value={hintItem.points}
                      onChange={(e) =>
                        handleModifyHint(index, hintItem.hint, e.target.value)
                      }
                    />
                    <Button
                      variant="outlined"
                      color="danger"
                      onClick={() => handleDeleteHint(index)}
                      startDecorator={<DeleteIcon />}
                    >
                      Delete
                    </Button>
                  </Stack>
                ))}

                <Stack spacing={2} direction="column" sx={{ padding: "20px" }}>
                  <FormControl id="hint" sx={{ width: "100%" }}>
                    <Typography level="title-lg" sx={{ mb: 2 }}>
                      Add Hints
                    </Typography>

                    {/* Form Label for Hint */}
                    <FormLabel sx={{ mb: 1 }}>
                      Add Hint for the Challenge
                    </FormLabel>
                    <Input
                      size="sm"
                      startDecorator={<TipsAndUpdatesIcon />}
                      placeholder="Enter hint"
                      type="text"
                      value={hint}
                      onChange={(e) => setHint(e.target.value)}
                      sx={{ mb: 2 }}
                    />

                    {/* Form Label for Points */}
                    <FormLabel sx={{ mb: 1 }}>
                      Add Cost for the Hint to unlock (Points)
                    </FormLabel>
                    <Input
                      size="sm"
                      startDecorator={<StarsIcon />}
                      placeholder="Enter points"
                      type="number"
                      value={hintPoints}
                      onChange={(e) => setHintPoints(e.target.value)}
                    />

                    {/* Add spacing between the inputs and the button */}
                    <Button
                      variant="outlined"
                      color="primary"
                      startDecorator={<AddIcon />}
                      onClick={handleAddHint}
                      sx={{ mt: 2 }}
                    >
                      Add Another Hint
                    </Button>
                  </FormControl>
                </Stack>

                {/* Save all hints */}
                <Button
                  variant="solid"
                  color="primary"
                  onClick={handleSaveHints}
                >
                  Save All Hints
                </Button>
              </Stack>
            </Box>
          </ModalDialog>
        </Modal>
      )}
    </>
  );
};

export default CTFChallengeTableInfoPage;
